// ============================================================
// AddExposureModal - pick what a target is EXPOSED to (v8.351.0).
//
// The Exposures counterpart of AddConditionModal: a row of presets
// (Painted, Shrouded, Breached, the Fear ladder…) and a builder for a
// custom one - an axis, a signed value or a flag, who it applies to,
// how many hits consume it, a stacking cap, a duration. It writes one
// `Ex:` code through `onAdd` and stays open, as the condition picker
// does: a target painted AND breached is two picks, not two openings.
//
// No immunity here. An exposure modifies OTHERS, so nothing is immune
// to wearing one - the picker offers everything to everybody.
// ============================================================
import { useState } from 'react';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import common from '../styles/common.module.css';
import styles from '../styles/AddConditionModal.module.css';
import clsx from '../utils/clsx';
import { CountField, ExposureCustomBuilder } from './ExposureCustomBuilder';
import { ExposurePresetPicker } from './ExposurePresetPicker';
import { presetExposureCode, type SharedExposureFields } from './exposureBuilder';
import type { ExposureMode } from '../engine/exposures';

export { exposureColour } from './exposureColour';

interface AddExposureModalProps {
  onAdd: (code: string) => void;
  onClose: () => void;
  /** The teams on the roster, for the scope select. */
  teams?: { tag: string; label?: string }[];
  /** Opened inside another editor (the weapon bench): a drawer in place, never a modal on a modal (plan armoury). */
  inline?: boolean;
}

/** The picker itself: the header, who reads it, the presets and the custom builder. */
function ExposurePickerBody({ onAdd, onClose, teams = [] }: AddExposureModalProps) {
  // Shared by presets and the builder.
  const [shared, setShared] = useState<SharedExposureFields>({ scope: 'all', mode: '', uses: 0 });
  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>Expose them to…</h3>
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
          &times;
        </button>
      </div>
      <SharedFields shared={shared} onChange={setShared} teams={teams} />
      <ExposurePresetPicker onPick={(id, rounds) => onAdd(presetExposureCode(id, rounds, shared))} />
      <ExposureCustomBuilder shared={shared} onAdd={onAdd} />
    </>
  );
}

function ExposureModal(props: AddExposureModalProps) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(props.onClose, trapRef);
  return (
    <div
      className={common.modal}
      onClick={backdrop.onBackdropClick}
      onKeyDown={(e) => e.key === 'Escape' && props.onClose()}
      role="presentation"
    >
      <div
        className={clsx(common.modalContent, styles.content)}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add exposure dialog"
        data-testid="add-exposure-modal"
      >
        <ExposurePickerBody {...props} />
      </div>
    </div>
  );
}

export default function AddExposureModal(props: AddExposureModalProps) {
  if (!props.inline) return <ExposureModal {...props} />;
  return (
    <section className={styles.drawer} aria-label="Add exposure" data-testid="add-exposure-modal">
      <ExposurePickerBody {...props} />
    </section>
  );
}

interface SharedFieldsProps {
  shared: SharedExposureFields;
  onChange: (next: SharedExposureFields) => void;
  teams: { tag: string; label?: string }[];
}

/**
 * WHO READS IT, against what (v8.368.7), and how many hits it survives -
 * shared by every pick below, so a whole ambush can be painted "for
 * Kill-team, one hit each" without setting it sixteen times.
 */
function SharedFields({ shared, onChange, teams }: SharedFieldsProps) {
  const set = (patch: Partial<SharedExposureFields>) => onChange({ ...shared, ...patch });
  return (
    <div className={styles.presetRow} style={{ gap: 8, marginBottom: 10 }}>
      <label className={common.label}>
        Applies to
        <select
          className={common.input}
          aria-label="Exposure scope"
          value={shared.scope}
          onChange={(e) => set({ scope: e.target.value })}
        >
          <option value="all">Everyone</option>
          <option value="hostile">Hostiles only</option>
          <option value="ally">Allies only</option>
          {teams.map((t) => (
            <option key={t.tag} value={t.tag}>
              Team {t.label ?? t.tag}
            </option>
          ))}
        </select>
      </label>
      <label className={common.label}>
        Against
        <select
          className={common.input}
          aria-label="Exposure against"
          value={shared.mode}
          onChange={(e) => set({ mode: e.target.value as '' | ExposureMode })}
          title="Only one kind of attack reads it"
        >
          <option value="">Any attack</option>
          <option value="melee">Melee only</option>
          <option value="ranged">Ranged only</option>
        </select>
      </label>
      <CountField
        label="Hits it survives"
        aria="Exposure uses"
        value={shared.uses}
        onChange={(uses) => set({ uses })}
      />
    </div>
  );
}
